/**
 * A fold: the whole line is the press, it says what is inside, and a chevron
 * says it opens. The study's first finding was a card header that did not look
 * like a control; this one is a button that reads as one.
 *
 * Its own module rather than the node card's, because the card's sections fold
 * too — `ui/Standing.tsx` puts its rule, edges, date and library search in one
 * (D335) — and the card imports those sections, so a section that imported the
 * fold back from the card would close an import cycle. The card re-exports it
 * for its existing callers.
 */

import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { Chevron } from './Chevron'

/**
 * Which folds a viewer keeps open, across nodes and reloads.
 *
 * Per viewer and nowhere else: this is how one person likes to read, not a
 * fact about the exploration, so it stays out of the document, the export and
 * the shared link. Closed is the default and the fallback — storage that
 * refuses (a private window, blocked site data) leaves the card calm, and the
 * press still applies for this tab through the fallback copy.
 */
const FOLDS_KEY = 'nv.cardFolds'
let foldsFallback: string[] = []

export function openFolds(): string[] {
  try {
    const raw = localStorage.getItem(FOLDS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return foldsFallback
  }
}

export function rememberFold(id: string, open: boolean, defaultOpen = false): void {
  // A fold that opens by default remembers being CLOSED, as `!id`, so the one
  // list says both — and a reader's close outlives the default.
  const mark = defaultOpen ? `!${id}` : id
  const next = openFolds()
    .filter((x) => x !== mark)
    .concat(open !== defaultOpen ? [mark] : [])
  foldsFallback = next
  try {
    localStorage.setItem(FOLDS_KEY, JSON.stringify(next))
  } catch {
    // Refused: the fallback above carries it for the life of this tab.
  }
}

/**
 * The body is hidden rather than unmounted, so `aria-controls` never points at
 * an id that is not in the document — `Field`'s rule in components/Controls.
 */
export function Fold({
  id,
  label,
  meta,
  defaultOpen = false,
  children,
}: {
  /** The remembered name — one per kind of fold, shared by every card. */
  id: string
  label: string
  meta?: string
  /** Open until the reader closes it — for the fold a card is read for (D347). */
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(() =>
    defaultOpen ? !openFolds().includes(`!${id}`) : openFolds().includes(id),
  )
  const bodyId = useId()
  return (
    <div className="cfold" data-open={open || undefined}>
      <button
        type="button"
        className="cfold-head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => {
          rememberFold(id, !open, defaultOpen)
          setOpen(!open)
        }}
      >
        <Chevron open={open} />
        <span className="cfold-label">{label}</span>
        {meta && <span className="cfold-meta">{meta}</span>}
      </button>
      <div className="cfold-body" id={bodyId} hidden={!open}>
        {children}
      </div>
    </div>
  )
}
